import { useEffect, useState } from "react";
import { Download, ChevronDown, CheckCircle2, XCircle, Circle } from "lucide-react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/common";

const COURSES = [["yellow-belt", "Yellow Belt"], ["green-belt", "Green Belt"], ["black-belt", "Black Belt"]];
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

const toCsv = (rows) => {
  const head = ["Name", "Email", "Status", "Parts watched", "Total parts", "Quick checks correct", "Quick checks answered", "Modules done", "Total modules", "Completion %", "Final exam %"];
  const sess = rows[0]?.modules.map((m) => `Session quiz: ${m.title}`) || [];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [r.name, r.email, r.status, r.parts_watched, r.total_parts, r.checks_correct, r.checks_answered, r.modules_done, r.total_modules, r.completion, r.exam_score ?? "", ...r.modules.map((m) => m.session_score ?? "")].map(esc).join(","));
  return [[...head, ...sess].map(esc).join(","), ...lines].join("\n");
};

const PartDot = ({ p }) => {
  const Icon = !p.watched ? Circle : p.correct === false ? XCircle : CheckCircle2;
  const color = !p.watched ? "text-slate-300" : p.correct === false ? "text-rose-500" : p.correct ? "text-emerald-600" : "text-[#D4AF37]";
  const label = !p.watched ? "Not watched" : !p.has_check ? "Watched" : p.correct == null ? "Watched, question not answered" : p.correct ? "Watched, answered correctly" : "Watched, answered wrong";
  return <span title={`${p.title}: ${label}`}><Icon className={`w-4 h-4 ${color}`} /></span>;
};

const Detail = ({ r }) => (
  <div className="space-y-3 p-4 bg-[#FAF8FC]" data-testid={`lp-detail-${r.user_id}`}>
    {r.modules.map((m) => (
      <div key={m.id} className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
        <div className="sm:w-72 font-semibold truncate">{m.title}</div>
        <div className="flex flex-wrap gap-1 flex-1">{m.parts.length ? m.parts.map((p) => <PartDot key={p.id} p={p} />) : <span className="text-xs text-[#8a7a92]">No video parts</span>}</div>
        <div className="text-xs text-[#6b5a73] sm:w-48 sm:text-right">
          Session quiz: {m.session_score == null ? "—" : `${m.session_score}%`} {m.session_attempts > 0 && `(${m.session_attempts} tries)`} {m.completed && <span className="text-emerald-700 font-semibold">· done</span>}
        </div>
      </div>
    ))}
  </div>
);

const Row = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t hover:bg-[#FAF8FC] cursor-pointer" onClick={() => setOpen(!open)} data-testid={`lp-row-${r.user_id}`}>
        <td className="p-3"><div className="font-semibold">{r.name}</div><div className="text-xs text-[#8a7a92]">{r.email}</div></td>
        <td className="p-3 whitespace-nowrap">{r.parts_watched}/{r.total_parts}</td>
        <td className="p-3 whitespace-nowrap">{r.checks_answered ? `${r.checks_correct}/${r.checks_answered}` : "—"}</td>
        <td className="p-3 whitespace-nowrap">{r.modules_done}/{r.total_modules}</td>
        <td className="p-3 min-w-[120px]">
          <div className="h-2 rounded bg-[#F4EEF7]"><div className="h-2 rounded bg-[#5B2A6E]" style={{ width: `${r.completion}%` }} /></div>
          <div className="text-xs mt-1">{r.completion}%</div>
        </td>
        <td className="p-3 whitespace-nowrap">{r.exam_score == null ? "—" : `${r.exam_score}%`}</td>
        <td className="p-3"><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></td>
      </tr>
      {open && <tr><td colSpan={7} className="p-0"><Detail r={r} /></td></tr>}
    </>
  );
};

export default function LearnerProgress() {
  const [slug, setSlug] = useState("black-belt");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { setRows(null); api.get(`/admin/progress`, { params: { course: slug } }).then((r) => setRows(r.data)); }, [slug]);
  const shown = (rows || []).filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: `learner-progress-${slug}.csv` }).click();
    URL.revokeObjectURL(url);
  };
  const watched = shown.reduce((a, r) => a + r.parts_watched, 0);
  const totalParts = shown.reduce((a, r) => a + r.total_parts, 0);

  return (
    <div>
      <PageHeader eyebrow="Academy" title="Learner progress" subtitle="See which staff have watched each video part, how they answered the quick checks, and their session quiz scores." />
      <div className="flex flex-wrap gap-2 mb-4">
        {COURSES.map(([s, l]) => <button key={s} onClick={() => setSlug(s)} className={s === slug ? "dx-btn-purple" : "dx-btn-ghost"} data-testid={`lp-course-${s}`}>{l}</button>)}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="dx-card p-4"><div className="dx-label">Learners</div><div className="text-2xl font-bold text-[#5B2A6E]" data-testid="lp-stat-learners">{shown.length}</div></div>
        <div className="dx-card p-4"><div className="dx-label">Parts watched</div><div className="text-2xl font-bold text-[#5B2A6E]" data-testid="lp-stat-watched">{pct(watched, totalParts)}%</div></div>
        <div className="dx-card p-4"><div className="dx-label">Finished course</div><div className="text-2xl font-bold text-[#5B2A6E]" data-testid="lp-stat-finished">{shown.filter((r) => r.status === "completed").length}</div></div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input className="dx-input sm:max-w-xs" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lp-search" />
        <button className="dx-btn-gold sm:ml-auto" disabled={!shown.length} onClick={download} data-testid="lp-export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
      </div>
      <div className="dx-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="lp-table">
          <thead className="text-left text-xs uppercase tracking-wide text-[#8a7a92]">
            <tr><th className="p-3">Learner</th><th className="p-3">Parts watched</th><th className="p-3">Quick checks</th><th className="p-3">Sessions</th><th className="p-3">Completion</th><th className="p-3">Final exam</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((r) => <Row key={r.user_id} r={r} />)}
            {rows && !shown.length && <tr><td colSpan={7} className="p-6 text-center text-[#8a7a92]" data-testid="lp-empty">No learners enrolled in this course yet.</td></tr>}
            {!rows && <tr><td colSpan={7} className="p-6 text-center text-[#8a7a92]">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
